import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * VL-349 — a cashier's login QR code. Mirrors `CashierLoginQrStatus` /
 * `CashierLoginQrIssued` in `packages/contracts/schemas/domain/cashier.ts`.
 *
 * Its own hook module (and route) rather than part of `use-employees`: issuing
 * a code is not an edit of the cashier.
 */
export type CashierLoginQrStatus = {
  cashierId: string;
  active: boolean;
  version: number | null;
  generatedAt: string | null;
};

export type CashierLoginQrIssued = CashierLoginQrStatus & {
  active: true;
  version: number;
  generatedAt: string;
  /** The code's text. Returned by the generate call only, never again. */
  payload: string;
  revokedVersion: number | null;
};

const KEY = (id: string) => ['employees', 'cashiers', id, 'login-qr'] as const;

export function useCashierLoginQr(id: string, enabled = true) {
  return useQuery({
    queryKey: KEY(id),
    enabled,
    queryFn: async (): Promise<CashierLoginQrStatus> => {
      const res = await apiClient.get<CashierLoginQrStatus>(`/employees/cashiers/${id}/login-qr`);
      return res.data;
    },
  });
}

export function useGenerateCashierLoginQr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<CashierLoginQrIssued> => {
      const res = await apiClient.post<CashierLoginQrIssued>(`/employees/cashiers/${id}/login-qr`);
      return res.data;
    },
    onSuccess: (_, id) => qc.invalidateQueries({ queryKey: KEY(id) }),
  });
}
